// Content for the evergreen explainer, famous-cases and meteor-shower pages. Shared by the
// React screens and the edge renderer (seo/render.ts), so crawlers get the same copy users read.

export const PAGES_UPDATED = '2026-10-30';

// ---------------------------------------------------------------- "What was that light?" explainers

export interface Explainer {
  slug: string;
  /** Short name, e.g. for cards and the identifier's "learn more" link. */
  name: string;
  title: string;
  /** Shorter variant for the <title> tag; falls back to `title`. */
  seoTitle?: string;
  description: string;
  intro: string;
  looksLike: string[];
  howToCheck: string[];
  faqs: Array<{ question: string; answer: string }>;
}

export const EXPLAINED_PATH = '/explained';
export const EXPLAINED_TITLE = 'What Was That Light in the Sky?';
export const EXPLAINED_DESCRIPTION =
  'The everyday things most often reported as UFOs, how to recognise each one, and how to check what was really in your sky.';

export const EXPLAINERS: Explainer[] = [
  {
    slug: 'starlink-satellite-train',
    name: 'Starlink satellite trains',
    title: 'Line of Lights Moving Across the Sky? It’s Probably a Starlink Train',
    seoTitle: 'Starlink Satellite Trains Explained',
    description: 'Why a straight line of evenly spaced lights glides silently across the sky after dark, and how to tell if it was Starlink.',
    intro:
      'A row of evenly spaced lights sliding silently across the night sky is one of the most common things reported as a UFO. It is almost always a “train” of SpaceX Starlink satellites. For a few weeks after each launch, the newly released satellites travel close together in a line while they climb to their working orbits, catching sunlight as they go.',
    looksLike: [
      'A straight line of 10 to 60 or more lights, one behind another.',
      'All moving at the same steady speed in the same direction, without blinking.',
      'Completely silent.',
      'Usually seen an hour or two after sunset or before sunrise, when the satellites are sunlit but the sky is dark.',
      'The lights may fade out one after another as they pass into Earth’s shadow.',
    ],
    howToCheck: [
      'Use our sky checker with the time and place: it looks for Starlink satellites passing on the same track.',
      'Check whether a Starlink launch happened in the previous few weeks.',
      'Aircraft blink and make noise; a Starlink train does neither.',
    ],
    faqs: [
      { question: 'Why are Starlink satellites in a line?', answer: 'They are released together from the same rocket and spread out gradually as they raise their orbits, so for the first few weeks they follow each other in a tight line.' },
      { question: 'Can you see Starlink every night?', answer: 'Individual Starlink satellites are visible most clear nights, but the striking “train” is only seen in the weeks after a launch.' },
    ],
  },
  {
    slug: 'satellites-and-the-iss',
    name: 'Satellites and the ISS',
    title: 'Steady Light Gliding Across the Sky? Satellites and the ISS Explained',
    seoTitle: 'Satellites and the ISS Explained',
    description: 'How to recognise a satellite or the International Space Station, why they vanish mid-sky, and how to check a pass.',
    intro:
      'A star-like point of light that glides smoothly and silently across the sky over a few minutes is usually a satellite. They shine by reflecting sunlight, so they are seen after dusk and before dawn. The International Space Station is the brightest of all, often outshining every star.',
    looksLike: [
      'A single steady point of light, with no blinking or coloured lights.',
      'Moving in a straight line at a constant speed, crossing much of the sky in a few minutes.',
      'Silent.',
      'Sometimes fading out or disappearing suddenly while still high up, as it enters Earth’s shadow.',
      'The ISS is very bright and can take up to about six minutes to cross.',
    ],
    howToCheck: [
      'Run our sky checker for the time and place; it calculates real satellite and space station passes.',
      'Note which way it was travelling and how high it got: these are what distinguish one pass from another.',
      'If it blinked or you heard engines, it was more likely an aircraft.',
    ],
    faqs: [
      { question: 'Why did the light just disappear?', answer: 'Satellites are lit by the Sun. When one moves into Earth’s shadow it stops reflecting sunlight and seems to vanish, even though it is still there.' },
      { question: 'How bright is the ISS?', answer: 'At its best the International Space Station is brighter than any star and only fainter than the Moon and Venus.' },
    ],
  },
  {
    slug: 'venus-and-jupiter',
    name: 'Venus and Jupiter',
    title: 'Very Bright “Star” That Doesn’t Move? It’s Probably Venus or Jupiter',
    seoTitle: 'Venus and Jupiter: Bright “Stars” at Night',
    description: 'Why the brightest planets are so often reported as UFOs, and how to check whether a planet was where you looked.',
    intro:
      'A dazzling light that hangs in one place for a long time is very often a planet. Venus is the brightest natural object in the night sky after the Moon, and Jupiter is not far behind. Low in the sky they can shimmer, change colour and seem to move, especially when you watch them for a while.',
    looksLike: [
      'Much brighter than any star.',
      'Staying in the same place for a long time, or drifting very slowly as the sky turns.',
      'Often low in the west after sunset or the east before sunrise (Venus).',
      'Shimmering or flashing colours when low, because of the atmosphere.',
      'Still there when you stop looking, and there again the next night.',
    ],
    howToCheck: [
      'Our sky checker shows which planets were up at your time and place, and in which direction.',
      'Look again the following night at the same time: a planet will be in almost the same spot.',
      'Binoculars show a planet as a small disc rather than a point.',
    ],
    faqs: [
      { question: 'Why does Venus look like it’s moving?', answer: 'When you stare at a bright point against a dark sky your eyes make tiny movements, so it can appear to wander. Clouds drifting past also make it seem to move.' },
      { question: 'When is Venus brightest?', answer: 'Venus is brightest around its greatest elongations, when it appears furthest from the Sun in the evening or morning sky.' },
    ],
  },
  {
    slug: 'twinkling-stars',
    name: 'Twinkling stars',
    title: 'Star Flashing Red, Green and Blue? Why Bright Stars Twinkle',
    seoTitle: 'Why Bright Stars Twinkle',
    description: 'Why bright stars near the horizon flash colours and seem to dance, and how to tell one from something stranger.',
    intro:
      'A bright star low in the sky can flash red, green and blue and seem to dance about. This is twinkling: the starlight passes through a lot of turbulent air near the horizon, which bends and splits it. Sirius, the brightest star in the night sky, is the most common culprit.',
    looksLike: [
      'A single point flashing several colours quickly.',
      'Low in the sky, often in the south or near the horizon.',
      'Seeming to jitter or wobble but never actually travelling across the sky.',
      'Still there, in the same place, for hours.',
    ],
    howToCheck: [
      'Our sky checker lists the brightest stars above the horizon at your time and place.',
      'Watch it against a fixed object like a chimney: over an hour it moves slowly with all the other stars.',
      'Higher in the sky, the same star twinkles much less.',
    ],
    faqs: [
      { question: 'Why do stars twinkle but planets don’t as much?', answer: 'Stars are so far away they are effectively single points, so the atmosphere disturbs their light easily. Planets are tiny discs, which smooths the effect out.' },
      { question: 'Which star flashes colours?', answer: 'Sirius is the most famous because it is the brightest, but any bright star low in the sky can do it.' },
    ],
  },
  {
    slug: 'aircraft-lights',
    name: 'Aircraft lights',
    title: 'Bright Light That Seems to Hover? How Aircraft Lights Fool People',
    seoTitle: 'How Aircraft Lights Fool People',
    description: 'How landing lights, strobes and navigation lights make ordinary aircraft look strange, and how to spot them.',
    intro:
      'Aircraft account for a large share of UFO reports. A plane flying straight towards you with its landing lights on can look like a brilliant light hanging motionless for minutes. At night, strobes and coloured navigation lights add to the effect.',
    looksLike: [
      'Flashing white strobes, a red light on the left wing and a green one on the right.',
      'A very bright, steady light that seems to hover, then slowly grows or turns.',
      'Engine noise, sometimes arriving after the plane has passed overhead.',
      'Several lights moving together when a few planes are lined up to land at a nearby airport.',
    ],
    howToCheck: [
      'If it is happening right now, our sky checker shows live aircraft within 50 km.',
      'Flight-tracking apps show aircraft positions in real time.',
      'Note whether there is an airport in that direction: landing approaches follow the same paths every day.',
    ],
    faqs: [
      { question: 'Why does a plane look like it is hovering?', answer: 'When an aircraft flies directly towards you its position in the sky barely changes, so its landing lights seem to hang still until it turns or gets close.' },
      { question: 'What do the colours on a plane mean?', answer: 'Red is on the left wingtip, green on the right, and white strobes and tail lights help other aircraft see it.' },
    ],
  },
  {
    slug: 'drones',
    name: 'Drones',
    title: 'Light Hovering, Stopping and Darting? It Could Be a Drone',
    seoTitle: 'Drone Lights: Hovering, Stopping, Darting',
    description: 'How drones look at night, why their movement seems impossible, and how to tell one from other lights.',
    intro:
      'Drones can hover, stop dead, change direction instantly and climb straight up: exactly the kind of movement people describe as impossible for an aircraft. Most carry bright LEDs that can be white, red, green or other colours, and some light shows use hundreds at once.',
    looksLike: [
      'Low in the sky, often over houses, fields or the coast.',
      'Hovering, then darting or changing direction sharply.',
      'Coloured or blinking LEDs.',
      'A buzz or whine when it is close enough to hear.',
      'Formations of many lights forming shapes during organised drone shows.',
    ],
    howToCheck: [
      'Listen: drones are rarely silent up close.',
      'Note how low it is relative to trees or buildings.',
      'Check whether a drone light show was scheduled nearby.',
    ],
    faqs: [
      { question: 'Can drones fly at night?', answer: 'Yes. In many countries they must show lights at night, which is often what people notice.' },
      { question: 'How high can drones go?', answer: 'Hobby drones are usually limited by law to around 120 metres (400 feet), so they appear low.' },
    ],
  },
  {
    slug: 'sky-lanterns',
    name: 'Sky lanterns',
    title: 'Orange Lights Drifting Across the Sky? They’re Probably Sky Lanterns',
    seoTitle: 'Sky Lanterns Explained',
    description: 'Why sky lanterns are reported as UFOs, how to recognise them, and when you are most likely to see them.',
    intro:
      'Groups of orange or amber lights drifting slowly, silently and slightly unevenly across the sky are very often paper sky lanterns, released at weddings, parties and celebrations. They rise, drift with the wind and then go out one by one as their flames die.',
    looksLike: [
      'Orange, amber or yellowish lights that flicker.',
      'Several together, drifting at different heights and speeds.',
      'Moving with the wind rather than in a straight purposeful line.',
      'Silent, then winking out suddenly one at a time.',
      'Most common on weekends, holidays and festival nights.',
    ],
    howToCheck: [
      'Compare their direction with the wind that evening.',
      'Note whether they went out one after another rather than all together.',
      'Check whether there was a wedding, festival or celebration nearby.',
    ],
    faqs: [
      { question: 'Why do sky lanterns suddenly disappear?', answer: 'The small flame inside burns out, so the light goes out at once while the lantern is still in the air.' },
      { question: 'Are sky lanterns legal?', answer: 'Rules vary by country and area. Some places ban or restrict them because of fire and livestock risks.' },
    ],
  },
  {
    slug: 'meteors-and-fireballs',
    name: 'Meteors and fireballs',
    title: 'Streak of Light With a Tail? Meteors and Fireballs Explained',
    seoTitle: 'Meteors and Fireballs Explained',
    description: 'What meteors and fireballs look like, why some glow green or break apart, and when they are most common.',
    intro:
      'A bright streak shooting across the sky in a second or two is a meteor: a speck of space rock burning up high in the atmosphere. Unusually bright ones, called fireballs, can light up the ground, glow green or orange and break into pieces.',
    looksLike: [
      'A fast streak lasting a second or a few seconds.',
      'A glowing tail or trail, sometimes lingering briefly.',
      'Green, orange or white colours, and sometimes fragments breaking off.',
      'Usually silent; very rarely a sonic boom follows minutes later.',
      'More frequent during annual meteor showers.',
    ],
    howToCheck: [
      'See whether a meteor shower was active on our meteor shower calendar.',
      'Fireballs are often seen by many people over a wide area, so look for other reports that night.',
      'A slower, longer-lasting fireball that breaks into a line of glowing pieces can be space debris re-entering.',
    ],
    faqs: [
      { question: 'What is the difference between a meteor and a fireball?', answer: 'A fireball is simply a very bright meteor, brighter than the planet Venus.' },
      { question: 'Why are some meteors green?', answer: 'The colour comes from the chemistry of the rock and the air it heats; green is often linked to oxygen and metals like nickel and magnesium.' },
    ],
  },
  {
    slug: 'balloons',
    name: 'Balloons',
    title: 'Silver Orb Hanging in the Daytime Sky? It Might Be a Balloon',
    seoTitle: 'Silver Orb in the Sky? Probably a Balloon',
    description: 'How party, weather and research balloons are mistaken for UFOs, and how to tell.',
    intro:
      'A bright silver or white sphere seen in daylight, drifting slowly or apparently hanging still, is often a balloon. Party balloons, weather balloons launched twice a day around the world, and high-altitude research balloons all catch the sunlight.',
    looksLike: [
      'A round, silvery or white object in daylight.',
      'Drifting slowly, or seeming to hang still for a long time.',
      'Silent.',
      'Sometimes moving against the wind at ground level, because winds higher up blow in a different direction.',
    ],
    howToCheck: [
      'Weather services launch balloons at fixed times (around 00:00 and 12:00 UTC) from known sites.',
      'Watch for a long time: a balloon drifts steadily and slowly with the upper winds.',
      'Binoculars may show a shape, a payload or a string.',
    ],
    faqs: [
      { question: 'How high do weather balloons go?', answer: 'They typically rise to around 30 kilometres before bursting, high enough to catch sunlight after it is dark on the ground.' },
      { question: 'Why does a balloon seem to move against the wind?', answer: 'Winds at different heights blow in different directions, so a high balloon can travel the opposite way to the breeze you feel.' },
    ],
  },
];

/** The identifier's result ids, mapped to the explainer that covers each one. */
export const EXPLAINER_FOR_CANDIDATE: Record<string, string> = {
  starlink: 'starlink-satellite-train',
  satellite: 'satellites-and-the-iss',
  planet: 'venus-and-jupiter',
  star: 'twinkling-stars',
  aircraft: 'aircraft-lights',
  drone: 'drones',
  lantern: 'sky-lanterns',
  meteor: 'meteors-and-fireballs',
  balloon: 'balloons',
};

// ---------------------------------------------------------------- famous cases

export const FAMOUS_PATH = '/famous-ufo-cases';
export const FAMOUS_TITLE = 'Famous UFO Cases: Historic Sightings and What Explains Them';
export const FAMOUS_DESCRIPTION =
  'Roswell, Rendlesham, the Phoenix Lights and more: what witnesses reported, what investigators concluded, and sources to read further.';

// ---------------------------------------------------------------- meteor showers

export interface Shower {
  name: string;
  active: string;
  peak: string;
  /** Approximate maximum meteors per hour under ideal dark skies (ZHR). */
  rate: string;
  parent: string;
  note: string;
}

export const SHOWERS_PATH = '/meteor-showers';
export const SHOWERS_TITLE = 'Meteor Shower Calendar: When to See Shooting Stars';
export const SHOWERS_DESCRIPTION =
  'The major annual meteor showers, when they peak, how many meteors to expect, and how not to mistake a fireball for a UFO.';

export const SHOWERS: Shower[] = [
  { name: 'Quadrantids', active: '28 Dec – 12 Jan', peak: '3–4 January', rate: 'up to ~110 per hour', parent: 'Asteroid 2003 EH1', note: 'A short, sharp peak lasting only a few hours. Best from the Northern Hemisphere.' },
  { name: 'Lyrids', active: '14 – 30 April', peak: '22 April', rate: '~18 per hour', parent: 'Comet Thatcher (C/1861 G1)', note: 'Modest, with occasional bright meteors that leave glowing trails.' },
  { name: 'Eta Aquariids', active: '19 April – 28 May', peak: '6 May', rate: '~50 per hour', parent: 'Halley’s Comet', note: 'Best from the Southern Hemisphere, in the hours before dawn.' },
  { name: 'Perseids', active: '17 July – 24 August', peak: '12 August', rate: '~100 per hour', parent: 'Comet Swift–Tuttle', note: 'The most popular shower of the year, on warm summer nights in the north. Known for bright fireballs.' },
  { name: 'Draconids', active: '6 – 10 October', peak: '8 October', rate: 'usually a few per hour', parent: 'Comet Giacobini–Zinner', note: 'Usually quiet, but has produced rare outbursts. Best in the evening.' },
  { name: 'Orionids', active: '2 October – 7 November', peak: '21 October', rate: '~20 per hour', parent: 'Halley’s Comet', note: 'Fast meteors, best after midnight.' },
  { name: 'Leonids', active: '6 – 30 November', peak: '17 November', rate: '~15 per hour', parent: 'Comet Tempel–Tuttle', note: 'Famous for historic meteor storms; most years are modest.' },
  { name: 'Geminids', active: '4 – 20 December', peak: '14 December', rate: 'up to ~150 per hour', parent: 'Asteroid 3200 Phaethon', note: 'Often the year’s strongest shower, with bright, slower meteors visible from early evening.' },
];
